import {
  DOCUMENT_SESSION_PROTOCOL_VERSION,
  normalizeDocumentSessionServerMessage,
  type DocumentProjectionDescriptor,
  type DocumentSessionBootstrap,
} from './documentSessionProtocol'

export const WORKBOOK_DOCUMENT_MANIFEST_VERSION = 1 as const
export const WORKBOOK_DOCUMENT_MANIFEST_ROOT_MAP = 'workbook_document_manifest_v1' as const

export type WorkbookDocumentTabKind = 'data_source' | 'worksheet' | 'spreadsheet' | 'cfo_core' | 'cfo_presentation'

export type WorkbookDocumentTabRef = {
  kind: WorkbookDocumentTabKind
  name?: string | null
  id?: string | null
  source_ref?: string | null
}

export type WorkbookDocumentSpreadsheetBinding = {
  tab_id: string
  engine_sheet_id?: string | null
  worker_sheet_id?: number | null
  sheet_name?: string | null
}

export type WorkbookDocumentManifestV1 = {
  version: typeof WORKBOOK_DOCUMENT_MANIFEST_VERSION
  workbook_id: string
  workbook_kind: 'spreadsheet' | 'tableau' | string
  name: string
  tab_order: WorkbookDocumentTabRef[]
  active_tab?: WorkbookDocumentTabRef | null
  spreadsheet: {
    sheet_bindings: WorkbookDocumentSpreadsheetBinding[]
  }
  tableau: {
    imports: unknown[]
    artifacts: unknown[]
    worksheets: unknown[]
    dashboards: unknown[]
  }
  named_resources: unknown
  migration: {
    status: string
    provenance: string[]
    migrated_at_epoch_ms?: number | null
  }
}

export type WorkbookOpCategory = 'workbook' | 'spreadsheet' | 'tableau' | 'projection'

export type WorkbookOpEnvelope = {
  op_id: string
  op_type: string
  category: WorkbookOpCategory
  payload: unknown
  created_at_epoch_ms?: number | null
}

export type WorkbookDocumentWorkbookMetadata = {
  id: string
  site_id: string
  site_slug?: string | null
  workbook_kind: 'spreadsheet' | 'tableau' | string
  name: string
  created_by: string
  created_at: string
  updated_at: string
  can_edit: boolean
  last_viewed_at?: string | null
  last_opened_tab?: WorkbookDocumentTabRef | null
  thumbnail_url?: string | null
  is_favorite?: boolean | null
}

export type WorkbookProjectionStatus = DocumentProjectionDescriptor

export type WorkbookBootstrap = {
  protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
  workbook_id: string
  server_time_epoch_ms: number
  workbook: WorkbookDocumentWorkbookMetadata
  manifest: WorkbookDocumentManifestV1
  manifest_source: 'document_session' | 'derived' | string
  active_tab?: WorkbookDocumentTabRef | null
  document_session: DocumentSessionBootstrap
  projection_statuses: WorkbookProjectionStatus[]
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

const normalizeSeq = (value: unknown): number => {
  const next = Number(value)
  if (!Number.isFinite(next) || next < 0) return 0
  return Math.floor(next)
}

export const normalizeWorkbookDocumentTabKind = (value: unknown): WorkbookDocumentTabKind | null => {
  const normalized = String(value ?? '').trim().toLowerCase().replace(/-/g, '_')
  if (normalized === 'data_source' || normalized === 'worksheet' || normalized === 'spreadsheet' || normalized === 'cfo_core' || normalized === 'cfo_presentation') {
    return normalized
  }
  return null
}

export const normalizeWorkbookDocumentTabRef = (value: unknown): WorkbookDocumentTabRef | null => {
  if (!isRecord(value)) return null
  const kind = normalizeWorkbookDocumentTabKind(value.kind)
  if (!kind) return null
  const rawName = String(value.name ?? '').trim()
  const name = rawName || (kind === 'cfo_core' ? 'CFO Core' : kind === 'cfo_presentation' ? 'Client Financials' : '')
  if ((kind === 'worksheet' || kind === 'spreadsheet') && !name) return null
  const id = String(value.id ?? '').trim()
  const sourceRef = String(value.source_ref ?? value.sourceRef ?? '').trim()
  return {
    kind,
    name: name || null,
    id: id || null,
    source_ref: sourceRef || null,
  }
}

const normalizeWorkbookKind = (value: unknown): string => {
  const raw = String(value ?? '').trim().toLowerCase()
  return raw === 'tableau' ? 'tableau' : 'spreadsheet'
}

const normalizeMetadata = (value: unknown): WorkbookDocumentWorkbookMetadata | null => {
  if (!isRecord(value)) return null
  const id = String(value.id ?? '').trim()
  if (!id) return null
  const lastOpenedTab = normalizeWorkbookDocumentTabRef(value.last_opened_tab ?? value.lastOpenedTab)
  return {
    id,
    site_id: String(value.site_id ?? value.siteId ?? '').trim(),
    site_slug: String(value.site_slug ?? value.siteSlug ?? '').trim() || null,
    workbook_kind: normalizeWorkbookKind(value.workbook_kind ?? value.workbookKind),
    name: String(value.name ?? '').trim() || 'Workbook',
    created_by: String(value.created_by ?? value.createdBy ?? '').trim(),
    created_at: String(value.created_at ?? value.createdAt ?? '').trim(),
    updated_at: String(value.updated_at ?? value.updatedAt ?? '').trim(),
    can_edit: Boolean(value.can_edit ?? value.canEdit),
    last_viewed_at: String(value.last_viewed_at ?? value.lastViewedAt ?? '').trim() || null,
    last_opened_tab: lastOpenedTab,
    thumbnail_url: String(value.thumbnail_url ?? value.thumbnailUrl ?? '').trim() || null,
    is_favorite: typeof value.is_favorite === 'boolean'
      ? value.is_favorite
      : (typeof value.isFavorite === 'boolean' ? value.isFavorite : null),
  }
}

const normalizeSpreadsheetBinding = (value: unknown): WorkbookDocumentSpreadsheetBinding | null => {
  if (!isRecord(value)) return null
  const tabId = String(value.tab_id ?? value.tabId ?? '').trim()
  if (!tabId) return null
  const workerSheetIdRaw = value.worker_sheet_id ?? value.workerSheetId
  const workerSheetId = Number(workerSheetIdRaw)
  return {
    tab_id: tabId,
    engine_sheet_id: String(value.engine_sheet_id ?? value.engineSheetId ?? '').trim() || null,
    worker_sheet_id: Number.isFinite(workerSheetId) && workerSheetId > 0 ? Math.floor(workerSheetId) : null,
    sheet_name: String(value.sheet_name ?? value.sheetName ?? '').trim() || null,
  }
}

export const normalizeWorkbookDocumentManifestV1 = (
  value: unknown,
  metadata?: WorkbookDocumentWorkbookMetadata | null,
): WorkbookDocumentManifestV1 | null => {
  if (!isRecord(value)) return null
  if (normalizeSeq(value.version) !== WORKBOOK_DOCUMENT_MANIFEST_VERSION) return null
  const workbookId = String(value.workbook_id ?? value.workbookId ?? metadata?.id ?? '').trim()
  if (!workbookId) return null
  const tabOrderRaw = value.tab_order ?? value.tabOrder
  const tabOrder = Array.isArray(tabOrderRaw)
    ? tabOrderRaw
      .map(normalizeWorkbookDocumentTabRef)
      .filter((tab): tab is WorkbookDocumentTabRef => Boolean(tab))
    : []
  const activeTab = normalizeWorkbookDocumentTabRef(value.active_tab ?? value.activeTab)
  const spreadsheetRecord = isRecord(value.spreadsheet) ? value.spreadsheet : {}
  const tableauRecord = isRecord(value.tableau) ? value.tableau : {}
  const migrationRecord = isRecord(value.migration) ? value.migration : {}
  return {
    version: WORKBOOK_DOCUMENT_MANIFEST_VERSION,
    workbook_id: workbookId,
    workbook_kind: normalizeWorkbookKind(value.workbook_kind ?? value.workbookKind ?? metadata?.workbook_kind),
    name: String(value.name ?? metadata?.name ?? '').trim() || 'Workbook',
    tab_order: tabOrder,
    active_tab: activeTab,
    spreadsheet: {
      sheet_bindings: (() => {
        const sheetBindingsRaw = spreadsheetRecord.sheet_bindings ?? spreadsheetRecord.sheetBindings
        return Array.isArray(sheetBindingsRaw)
          ? sheetBindingsRaw
          .map(normalizeSpreadsheetBinding)
          .filter((binding): binding is WorkbookDocumentSpreadsheetBinding => Boolean(binding))
          : []
      })(),
    },
    tableau: {
      imports: Array.isArray(tableauRecord.imports) ? tableauRecord.imports : [],
      artifacts: Array.isArray(tableauRecord.artifacts) ? tableauRecord.artifacts : [],
      worksheets: Array.isArray(tableauRecord.worksheets) ? tableauRecord.worksheets : [],
      dashboards: Array.isArray(tableauRecord.dashboards) ? tableauRecord.dashboards : [],
    },
    named_resources: value.named_resources ?? value.namedResources ?? {},
    migration: {
      status: String(migrationRecord.status ?? '').trim() || 'unknown',
      provenance: Array.isArray(migrationRecord.provenance)
        ? migrationRecord.provenance.map((entry) => String(entry ?? '').trim()).filter(Boolean)
        : [],
      migrated_at_epoch_ms: migrationRecord.migrated_at_epoch_ms == null
        ? null
        : normalizeSeq(migrationRecord.migrated_at_epoch_ms),
    },
  }
}

const normalizeProjectionStatus = (value: unknown): WorkbookProjectionStatus | null => {
  if (!isRecord(value)) return null
  const kind = String(value.kind ?? '').trim()
  if (!kind) return null
  return {
    kind,
    version: normalizeSeq(value.version) || 1,
    uri: String(value.uri ?? '').trim() || null,
    applied_op_seq: normalizeSeq(value.applied_op_seq ?? value.appliedOpSeq),
    metadata: value.metadata ?? null,
  }
}

export const normalizeWorkbookBootstrap = (
  input: unknown,
  requestedWorkbookIdRaw?: string | null,
): WorkbookBootstrap | null => {
  if (!isRecord(input)) return null
  const documentSession = normalizeDocumentSessionServerMessage({
    ...(isRecord(input.document_session) ? input.document_session : {}),
    type: 'bootstrap_ready',
  })
  if (!documentSession || documentSession.type !== 'bootstrap_ready') return null
  const workbook = normalizeMetadata(input.workbook)
  if (!workbook) return null
  const manifest = normalizeWorkbookDocumentManifestV1(input.manifest, workbook)
  if (!manifest) return null
  const workbookId = String(input.workbook_id ?? input.workbookId ?? workbook.id ?? '').trim()
  const requestedWorkbookId = String(requestedWorkbookIdRaw ?? '').trim()
  if (!workbookId || (requestedWorkbookId && workbookId !== requestedWorkbookId)) return null
  if (documentSession.workbook_id !== workbookId || workbook.id !== workbookId || manifest.workbook_id !== workbookId) {
    return null
  }
  const projectionStatusesRaw = input.projection_statuses ?? input.projectionStatuses
  const projectionStatuses = Array.isArray(projectionStatusesRaw)
    ? projectionStatusesRaw
      .map(normalizeProjectionStatus)
      .filter((status): status is WorkbookProjectionStatus => Boolean(status))
    : documentSession.projection_descriptors
  return {
    protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
    workbook_id: workbookId,
    server_time_epoch_ms: normalizeSeq(input.server_time_epoch_ms ?? input.serverTimeEpochMs),
    workbook,
    manifest,
    manifest_source: String(input.manifest_source ?? input.manifestSource ?? '').trim() || 'unknown',
    active_tab: normalizeWorkbookDocumentTabRef(input.active_tab ?? input.activeTab) ?? manifest.active_tab ?? null,
    document_session: {
      protocol_version: documentSession.protocol_version,
      workbook_id: documentSession.workbook_id,
      session_id: documentSession.session_id,
      snapshot_seq: documentSession.snapshot_seq,
      snapshot_payload_b64: documentSession.snapshot_payload_b64,
      chunk_snapshot_payloads_b64: documentSession.chunk_snapshot_payloads_b64,
      tail: documentSession.tail,
      chunk_descriptors: documentSession.chunk_descriptors,
      projection_descriptors: documentSession.projection_descriptors,
      lease: documentSession.lease,
    },
    projection_statuses: projectionStatuses,
  }
}
